import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, makeStyles, Chip, CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field } from "formik";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import AppSelect from "components/Select/AppSelect";
import AppDialog from "components/Dialog/AppDialog";
import AppInput from "components/Input/AppInput";
import CustomDatePicker from "components/Input/CustomDatePicker";
import SelectField from "components/Select/SelectField";
import PreviewDialog from "components/Dialog/PreviewDialog";
import { showToast } from "store/slices/toast";
import {
  fetchPageAction,
  createAction,
  updateActionStatus,
  getActionList,
} from "store/slices/action";
import { fetchCompanyUserListPaging, getCompanyUserListPaging } from "store/slices/companyUser";
import { fetchWorkerListPaging, getWorkerList } from "store/slices/worker";
import { getAuthInfo } from "store/slices/auth";
import { fetchPageIncident, getIncidentList } from "store/slices/incident";
import { fetchPageComplaint, getComplaintList } from "store/slices/complaint";
import { fetchPageInvestigation, getInvestigationList } from "store/slices/investigation";
import { fetchClientListPaging, getClientList } from "store/slices/client";
import { ACTION_SOURCE, ACTION_STATUS, ACTION_OWNER } from "config/AppConfig";

const LINKED_RECORD_SOURCES = [
  ACTION_SOURCE.INCIDENT,
  ACTION_SOURCE.COMPLAINT,
  ACTION_SOURCE.INVESTIGATION,
  ACTION_SOURCE.PARTICIPANT,
  ACTION_SOURCE.STAFF,
];

const ADD_ACTION_SOURCES = [
  ACTION_SOURCE.INCIDENT,
  ACTION_SOURCE.INVESTIGATION,
  ACTION_SOURCE.COMPLAINT,
  ACTION_SOURCE.PARTICIPANT,
  ACTION_SOURCE.STAFF,
];

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/jpg',
];
const ALLOWED_FILE_ACCEPT = ".docx,.pdf,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/jpg";

const useStyles = makeStyles(() => ({
  container: {
    padding: "20px",
    backgroundColor: '#F5F5F5'
  },
  titleRow: {
    marginBottom: "14px",
  },
  mainTitle: {
    fontSize: "22px",
    fontWeight: 700,
    marginBottom: "4px",
    color: "#0F766E",
  },
  subtitle: {
    fontSize: "12px",
    color: "#6B7280",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4,1fr)",
    gap: "8px",
    marginBottom: "14px",
  },
  statCard: {
    borderRadius: "8px",
    padding: "10px",
    textAlign: "center",
  },
  statNumber: {
    fontSize: "18px",
    fontWeight: 700,
    marginBottom: "4px",
  },
  statLabel: {
    fontSize: "10px",
  },
  filtersRow: {
    display: "flex",
    gap: "8px",
    marginBottom: "14px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  tableCard: {
    background: "#fff",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#6B7280",
    borderBottom: "1px solid #E5E7EB",
    background: "#FAFAFA",
    "&:first-child": {
      paddingLeft: "12px",
    },
  },
  td: {
    padding: "8px 10px",
    color: "#111827",
    fontSize: "12px",
    borderBottom: "1px solid #F3F4F6",
    "&:first-child": {
      paddingLeft: "12px",
    },
  },
  overdueRow: {
    background: "#FFF5F5",
  },
  completeRow: {
    background: "#F0FDFA",
  },
  badgeRed: {
    background: "#FEE2E2",
    color: "#DC2626",
  },
  badgeGreen: {
    background: "#CCFBF1",
    color: "#0F766E",
  },
  badgeAmber: {
    background: "#FEF3C7",
    color: "#92400E",
  },
  badgeBlue: {
    background: "#DBEAFE",
    color: "#1E40AF",
  },
  badgePurple: {
    background: "#EDE9FE",
    color: "#5B21B6",
  },
  badgeTeal: {
    background: "#CCFBF1",
    color: "#0F766E",
  },
  badgeGrey: {
    background: "#F3F4F6",
    color: "#6B7280",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: "20px",
    marginBottom: "10px",
    gap: "8px",
  },
  emptyCell: {
    textAlign: "center",
    padding: "24px",
    color: "#888780",
  },
}));

const sourceLabels = {
  [ACTION_SOURCE.INCIDENT]: "Incident",
  [ACTION_SOURCE.INVESTIGATION]: "Investigation",
  [ACTION_SOURCE.COMPLAINT]: "Complaint",
  [ACTION_SOURCE.AUDIT_FINDING]: "Audit finding",
  [ACTION_SOURCE.SUPERVISION]: "Supervision",
  [ACTION_SOURCE.GOVERNANCE_KPI]: "Governance KPI",
  [ACTION_SOURCE.HR]: "HR",
  [ACTION_SOURCE.PARTICIPANT]: "Participant",
  [ACTION_SOURCE.STAFF]: "Staff",
  [ACTION_SOURCE.COMPLIANCE]: "Compliance",
  [ACTION_SOURCE.OTHER]: "Other",
};

const statusLabels = {
  [ACTION_STATUS.OPEN]: "Open",
  [ACTION_STATUS.IN_PROGRESS]: "In progress",
  [ACTION_STATUS.COMPLETED]: "Complete",
  [ACTION_STATUS.OVERDUE]: "Overdue",
};

const ownerLabels = {
  [ACTION_OWNER.WORKER]: "Worker",
  [ACTION_OWNER.PARTICIPANT]: "Participant",
  [ACTION_OWNER.COMPANY]: "Company",
};

const sourceOptions = [
  { value: null, label: "All sources" },
  ...Object.values(ACTION_SOURCE).map((value) => ({ value, label: sourceLabels[value] })),
];

const statusOptions = [
  { value: null, label: "All status" },
  ...Object.values(ACTION_STATUS).map((value) => ({ value, label: statusLabels[value] })),
];

const ownerOptions = [
  { value: null, label: "All owners" },
  ...Object.values(ACTION_OWNER).map((value) => ({ value, label: ownerLabels[value] })),
];

const getSourceBadgeClass = (classes, source) => {
  switch (source) {
    case ACTION_SOURCE.HR:
    case ACTION_SOURCE.STAFF:
    case ACTION_SOURCE.COMPLIANCE:
    case ACTION_SOURCE.PARTICIPANT:
      return classes.badgeBlue;
    case ACTION_SOURCE.AUDIT_FINDING:
    case ACTION_SOURCE.GOVERNANCE_KPI:
      return classes.badgeAmber;
    case ACTION_SOURCE.INCIDENT:
    case ACTION_SOURCE.INVESTIGATION:
      return classes.badgeTeal;
    default:
      return classes.badgePurple;
  }
};

const getStatusBadgeClass = (classes, status) => {
  switch (status) {
    case ACTION_STATUS.OVERDUE:
      return classes.badgeRed;
    case ACTION_STATUS.OPEN:
      return classes.badgeAmber;
    case ACTION_STATUS.IN_PROGRESS:
      return classes.badgeBlue;
    case ACTION_STATUS.COMPLETED:
      return classes.badgeTeal;
    default:
      return classes.badgeAmber;
  }
};

const getRowClass = (classes, action) => {
  if (action.status === ACTION_STATUS.OVERDUE) {
    return classes.overdueRow;
  }
  if (action.status === ACTION_STATUS.COMPLETED) {
    return classes.completeRow;
  }
  return "";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatInputDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isWithinLast30Days = (dateStr) => {
  if (!dateStr) return true;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return true;
  const diffDays = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays <= 30;
};

const AddActionDialog = ({ open, onClose, onConfirm, companyUserOptions, workerOptions }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const hiddenFileInputRef = useRef(null);
  const [localFile, setLocalFile] = useState(null);
  const [linkedSource, setLinkedSource] = useState(ACTION_SOURCE.INCIDENT);

  const authUser = useSelector(getAuthInfo);
  const incidentList = useSelector(getIncidentList);
  const complaintList = useSelector(getComplaintList);
  const investigationList = useSelector(getInvestigationList);
  const clientList = useSelector(getClientList);

  useEffect(() => {
    if (!open) {
      setLocalFile(null);
      setLinkedSource(ACTION_SOURCE.INCIDENT);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (linkedSource === ACTION_SOURCE.INCIDENT) {
      dispatch(fetchPageIncident({ page: 1, size: 200 }));
    } else if (linkedSource === ACTION_SOURCE.COMPLAINT) {
      dispatch(fetchPageComplaint({ page: 1, size: 200 }));
    } else if (linkedSource === ACTION_SOURCE.INVESTIGATION) {
      dispatch(fetchPageInvestigation({ page: 1, size: 200 }));
    } else if (linkedSource === ACTION_SOURCE.PARTICIPANT) {
      dispatch(fetchClientListPaging({ page: 1, size: 200, companyId: authUser?.companyId }));
    }
  }, [open, linkedSource, dispatch, authUser]);

  const linkedRecordOptions = (() => {
    switch (linkedSource) {
      case ACTION_SOURCE.INCIDENT:
        return (incidentList || []).map((i) => ({ value: i.id, label: i.incidentNumber || `INC-${i.id}` }));
      case ACTION_SOURCE.COMPLAINT:
        return (complaintList || []).map((c) => ({ value: c.id, label: c.complaintNumber || `CMP-${c.id}` }));
      case ACTION_SOURCE.INVESTIGATION:
        return (investigationList || []).map((i) => ({ value: i.id, label: i.title || i.subject || `INV-${i.id}` }));
      case ACTION_SOURCE.PARTICIPANT:
        return (clientList || []).map((p) => ({ value: p.id, label: p.name }));
      case ACTION_SOURCE.STAFF:
        return workerOptions || [];
      default:
        return [];
    }
  })();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        dispatch(showToast({ type: 'error', message: 'Only DOCX, PDF, PNG or JPEG files are allowed' }));
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        dispatch(showToast({ type: 'error', message: 'File size must not exceed 10MB' }));
        return;
      }
      setLocalFile(file);
    }
  };

  return (
    <AppDialog
      open={open}
      title="Add Action"
      onClose={onClose}
      maxWidth="sm"
    >
      <Box>
        <Formik
          enableReinitialize
          initialValues={{
            source: ACTION_SOURCE.INCIDENT,
            linkedRecordId: "",
            actionDescription: "",
            owner: "",
            assignedToUserId: "",
            dueDate: "",
          }}
          validate={(values) => {
            const errors = {};
            const hideOwnerFields = values.source === ACTION_SOURCE.PARTICIPANT || values.source === ACTION_SOURCE.STAFF;
            if (!values.source) errors.source = "Required";
            if (!values.actionDescription) errors.actionDescription = "Required";
            if (!hideOwnerFields) {
              if (!values.owner) errors.owner = "Required";
              if (!values.dueDate) errors.dueDate = "Required";
              if (
                (values.owner === ACTION_OWNER.WORKER || values.owner === ACTION_OWNER.COMPANY) &&
                !values.assignedToUserId
              ) {
                errors.assignedToUserId = "Required";
              }
            }
            return errors;
          }}
          onSubmit={(values) => {
            onConfirm({ ...values, file: localFile });
          }}
        >
          {({ values, setFieldValue }) => {
            const hideOwnerFields = values.source === ACTION_SOURCE.PARTICIPANT || values.source === ACTION_SOURCE.STAFF;
            return (
            <Form>
              <div className="row">
                <div className="col-sm-12 col-md-6 mb-3">
                  <Field
                    component={SelectField}
                    name="source"
                    label="Source"
                    required
                    options={ADD_ACTION_SOURCES.map((value) => ({ value, label: sourceLabels[value] }))}
                    onSelected={(value) => {
                      setLinkedSource(value);
                      setFieldValue('linkedRecordId', '');
                    }}
                  />
                </div>
                <div className="col-sm-12 col-md-6 mb-3">
                  {LINKED_RECORD_SOURCES.includes(values.source) ? (
                    <Field
                      component={SelectField}
                      name="linkedRecordId"
                      label="Linked record"
                      placeholder="Select linked record"
                      options={linkedRecordOptions}
                    />
                  ) : (
                    <Field
                      component={AppInput}
                      name="linkedRecordId"
                      label="Linked record ID"
                      placeholder="Optional"
                    />
                  )}
                </div>

                <div className="col-sm-12 col-md-12 mb-3">
                  <Field
                    component={AppInput}
                    name="actionDescription"
                    label="Action description"
                    required
                    multiline
                    rows={3}
                    type="textarea"
                    placeholder="Describe the action to be taken in detail..."
                  />
                </div>

                {!hideOwnerFields && (
                  <div className="col-sm-12 col-md-6 mb-3">
                    <Field
                      component={SelectField}
                      name="owner"
                      label="Owner"
                      required
                      options={[
                        { value: ACTION_OWNER.WORKER, label: "Worker" },
                        { value: ACTION_OWNER.PARTICIPANT, label: "Participant" },
                        { value: ACTION_OWNER.COMPANY, label: "Company" },
                      ]}
                      onSelected={() => setFieldValue('assignedToUserId', '')}
                    />
                  </div>
                )}

                {!hideOwnerFields && (
                  <div className="col-sm-12 col-md-6 mb-3">
                    <Field
                      component={CustomDatePicker}
                      label="Due date"
                      name="dueDate"
                      type="text"
                      formatDateJob={true}
                      required
                      classInput="date-picker-container-custom"
                    />
                  </div>
                )}

                {!hideOwnerFields && (values.owner === ACTION_OWNER.WORKER || values.owner === ACTION_OWNER.COMPANY) && (
                  <div className="col-sm-12 col-md-6 mb-3">
                    <Field
                      component={SelectField}
                      name="assignedToUserId"
                      label="Assigned to"
                      required
                      options={(values.owner === ACTION_OWNER.WORKER ? workerOptions : companyUserOptions) || []}
                    />
                  </div>
                )}
              </div>

              <Box marginTop="6px" marginBottom="16px">
                <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: "#888780", marginBottom: "6px", textTransform: "uppercase" }}>
                  Evidence / attachment
                </label>
                <Box
                  style={{ border: "1.5px dashed #c5c4bc", borderRadius: "8px", padding: "16px", textAlign: "center", cursor: "pointer" }}
                  onClick={() => hiddenFileInputRef.current?.click()}
                >
                  <i className="fas fa-upload" style={{ fontSize: "18px", color: "#888780" }} />
                  <Box style={{ fontSize: "12px", color: "#5f5e5a", marginTop: "6px" }}>
                    {localFile ? localFile.name : "Drag and drop or browse"}
                  </Box>
                  <Box style={{ fontSize: "11px", color: "#888780", marginTop: "2px" }}>
                    DOCX, PDF, PNG, JPEG — max 10MB
                  </Box>
                </Box>
                <input
                  accept={ALLOWED_FILE_ACCEPT}
                  style={{ display: "none" }}
                  type="file"
                  ref={hiddenFileInputRef}
                  onChange={handleFileChange}
                />
              </Box>

              <Box className={classes.control}>
                <AppButton
                  label="Cancel"
                  onClick={onClose}
                  buttonColor="#F3F4F6"
                  rounder
                  style={{ color: "#111827" }}
                />
                <AppButton
                  label="Save action"
                  rounder
                  type="submit"
                  buttonColor={AppColor.mainColor}
                />
              </Box>
            </Form>
            );
          }}
        </Formik>
      </Box>
    </AppDialog>
  );
};

const EditActionDialog = ({ open, action, onClose, onConfirm }) => {
  const [status, setStatus] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [file, setFile] = useState(null);
  const dispatch = useDispatch();
  const hiddenFileInputRef = useRef(null);

  useEffect(() => {
    if (action) {
      setStatus(action.status || "");
      setDueDate(formatInputDate(action.dueDate));
      setFile(null);
    }
  }, [action, open]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      if (!ALLOWED_FILE_TYPES.includes(selected.type)) {
        dispatch(showToast({ type: 'error', message: 'Only DOCX, PDF, PNG or JPEG files are allowed' }));
        return;
      }
      if (selected.size > MAX_FILE_SIZE_BYTES) {
        dispatch(showToast({ type: 'error', message: 'File size must not exceed 10MB' }));
        return;
      }
      setFile(selected);
    }
  };

  if (!action) return null;

  return (
    <AppDialog
      open={open}
      title="Update Action"
      onClose={onClose}
      maxWidth="sm"
      actions={[
        { label: "Cancel", onPress: onClose, mode: "outlined" },
        {
          label: "Save",
          onPress: () => onConfirm({ status, dueDate, file }),
          buttonColor: AppColor.mainColor,
        },
      ]}
    >
      <Box>
        <Typography style={{ fontSize: 13, marginBottom: 12 }}>{action.actionDescription}</Typography>

        <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: "#888780", marginBottom: "6px", textTransform: "uppercase" }}>
          Status
        </label>
        <AppSelect
          name="status"
          placeholder="Status"
          isNotMargin={true}
          value={status ? { value: status, label: statusLabels[status] } : null}
          options={Object.values(ACTION_STATUS).map((value) => ({ value, label: statusLabels[value] }))}
          onChange={(option) => setStatus(option?.value || "")}
        />

        <Box marginTop="16px">
          <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: "#888780", marginBottom: "6px", textTransform: "uppercase" }}>
            Due date
          </label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #c5c4bc" }}
          />
        </Box>

        <Box marginTop="16px">
          <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: "#888780", marginBottom: "6px", textTransform: "uppercase" }}>
            Evidence / attachment
          </label>
          <Box
            style={{ border: "1.5px dashed #c5c4bc", borderRadius: "8px", padding: "16px", textAlign: "center", cursor: "pointer" }}
            onClick={() => hiddenFileInputRef.current?.click()}
          >
            <i className="fas fa-upload" style={{ fontSize: "18px", color: "#888780" }} />
            <Box style={{ fontSize: "12px", color: "#5f5e5a", marginTop: "6px" }}>
              {file ? file.name : (action.fileName || "Drag and drop or browse")}
            </Box>
            <Box style={{ fontSize: "11px", color: "#888780", marginTop: "2px" }}>
              DOCX, PDF, PNG, JPEG — max 10MB
            </Box>
          </Box>
          <input
            accept={ALLOWED_FILE_ACCEPT}
            style={{ display: "none" }}
            type="file"
            ref={hiddenFileInputRef}
            onChange={handleFileChange}
          />
        </Box>
      </Box>
    </AppDialog>
  );
};

const ActionRegisterTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const actions = useSelector(getActionList);
  const companyUsers = useSelector(getCompanyUserListPaging);
  const workers = useSelector(getWorkerList);
  const authUser = useSelector(getAuthInfo);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  const loadActions = () => {
    setIsLoading(true);
    dispatch(fetchPageAction({ page: 1, size: 200, sortField: "createdDate", sort: false })).finally(() => {
      if (isMountedRef.current) setIsLoading(false);
    });
  };

  useEffect(() => {
    loadActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (authUser && authUser.companyId) {
      dispatch(fetchCompanyUserListPaging({ company_id: authUser.companyId, page_number: 1, page_size: 1000 }));
      dispatch(fetchWorkerListPaging({ companyId: authUser.companyId, page: 1, size: 1000 }));
    }
  }, [dispatch, authUser]);

  const companyUserOptions = (companyUsers || []).map((u) => ({
    value: u.id,
    label: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
  }));

  const workerOptions = (workers || []).map((w) => ({
    value: w.id,
    label: w.name || `${w.firstName || ""} ${w.lastName || ""}`.trim(),
  }));

  const getAssignedName = (action) => {
    if (action.assignedToName) return action.assignedToName;
    if (action.assignedToUserId) {
      const worker = (workers || []).find((w) => w.id === action.assignedToUserId);
      if (worker) return worker.name || `${worker.firstName || ""} ${worker.firstName || ""}`.trim();
      const user = (companyUsers || []).find((u) => u.id === action.assignedToUserId);
      if (user) return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    }
    return ownerLabels[action.owner] || action.owner || "—";
  };

  const list = actions || [];

  const filteredActions = list.filter((action) => {
    if (selectedSource && action.source !== selectedSource) return false;
    if (selectedStatus && action.status !== selectedStatus) return false;
    if (selectedOwner && action.owner !== selectedOwner) return false;
    return true;
  });

  const stats = {
    overdue: list.filter((a) => a.status === ACTION_STATUS.OVERDUE).length,
    open: list.filter((a) => a.status === ACTION_STATUS.OPEN).length,
    inProgress: list.filter((a) => a.status === ACTION_STATUS.IN_PROGRESS).length,
    completed: list.filter((a) => a.status === ACTION_STATUS.COMPLETED && isWithinLast30Days(a.updatedAt)).length,
  };

  const handleAddAction = (values) => {
    const hideOwnerFields = values.source === ACTION_SOURCE.PARTICIPANT || values.source === ACTION_SOURCE.STAFF;
    const owner = hideOwnerFields
      ? (values.source === ACTION_SOURCE.PARTICIPANT ? ACTION_OWNER.PARTICIPANT : ACTION_OWNER.WORKER)
      : values.owner;
    const assignedToUserId = hideOwnerFields
      ? (values.linkedRecordId || null)
      : ((values.owner === ACTION_OWNER.WORKER || values.owner === ACTION_OWNER.COMPANY) ? values.assignedToUserId : null);
    dispatch(createAction({
      source: values.source,
      linkedRecordId: values.linkedRecordId || null,
      actionDescription: values.actionDescription,
      owner,
      assignedToUserId,
      dueDate: values.dueDate ? `${values.dueDate} 23:59` : null,
      file: values.file,
    })).then((res) => {
      if (!res.error) {
        setIsAddOpen(false);
        loadActions();
      }
    });
  };

  const handleUpdateAction = ({ status, dueDate, file }) => {
    dispatch(updateActionStatus({
      id: editingAction.id,
      status,
      dueDate: dueDate ? `${dueDate.replace(/-/g, "/")} 23:59` : null,
      file,
    })).then((res) => {
      if (!res.error) {
        setEditingAction(null);
        loadActions();
      }
    });
  };

  return (
    <Box className={classes.container}>

      <Box className={classes.titleRow}>
        <Typography className={classes.mainTitle} style={{ color: AppColor.mainColor }}>
          Action register
        </Typography>
        <Typography className={classes.subtitle}>
          All recommendations, audit findings, complaint outcomes and meeting actions in one place
        </Typography>
      </Box>

      <Box className={classes.filtersRow}>
        <AppButton
          label="+ Add action"
          buttonColor={AppColor.mainColor}
          rounder
          style={{ fontSize: 14, height: 41, minHeight: 41 }}
          onClick={() => setIsAddOpen(true)}
        />
        <Box style={{ minWidth: "150px" }}>
          <AppSelect
            name="sourceFilter"
            placeholder="All sources"
            value={selectedSource ? { value: selectedSource, label: sourceLabels[selectedSource] } : null}
            isNotMargin={true}
            options={sourceOptions}
            onChange={option => setSelectedSource(option?.value)}
          />
        </Box>
        <Box style={{ minWidth: "150px" }}>
          <AppSelect
            name="statusFilter"
            placeholder="All status"
            value={selectedStatus ? { value: selectedStatus, label: statusLabels[selectedStatus] } : null}
            isNotMargin={true}
            options={statusOptions}
            onChange={option => setSelectedStatus(option?.value)}
          />
        </Box>
        <Box style={{ minWidth: "150px" }}>
          <AppSelect
            name="ownerFilter"
            placeholder="All owners"
            value={selectedOwner ? { value: selectedOwner, label: ownerLabels[selectedOwner] } : null}
            isNotMargin={true}
            options={ownerOptions}
            onChange={option => setSelectedOwner(option?.value)}
          />
        </Box>
      </Box>

      <Box className={classes.statsGrid}>
        <Box className={classes.statCard} style={{ background: "#FEE2E2" }}>
          <Typography className={classes.statNumber} style={{ color: "#DC2626" }}>{stats.overdue}</Typography>
          <Typography className={classes.statLabel} style={{ color: "#991B1B" }}>Overdue</Typography>
        </Box>
        <Box className={classes.statCard} style={{ background: "#FEF3C7" }}>
          <Typography className={classes.statNumber} style={{ color: "#D97706" }}>{stats.open}</Typography>
          <Typography className={classes.statLabel} style={{ color: "#92400E" }}>Open</Typography>
        </Box>
        <Box className={classes.statCard} style={{ background: "#DBEAFE" }}>
          <Typography className={classes.statNumber} style={{ color: "#1E40AF" }}>{stats.inProgress}</Typography>
          <Typography className={classes.statLabel} style={{ color: "#1E40AF" }}>In progress</Typography>
        </Box>
        <Box className={classes.statCard} style={{ background: "#CCFBF1" }}>
          <Typography className={classes.statNumber} style={{ color: "#0F766E" }}>{stats.completed}</Typography>
          <Typography className={classes.statLabel} style={{ color: "#0F766E" }}>Completed (30d)</Typography>
        </Box>
      </Box>

      <Box className={classes.tableCard}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={classes.th}>Action</th>
              <th style={{ width: "130px" }} className={classes.th}>Source</th>
              <th style={{ width: "150px" }} className={classes.th}>Owner</th>
              <th style={{ width: "140px" }} className={classes.th}>Due date</th>
              <th style={{ width: "120px" }} className={classes.th}>Status</th>
              <th style={{ width: "140px" }} className={classes.th}>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className={classes.emptyCell}>
                  <CircularProgress size={20} style={{ color: AppColor.mainColor }} />
                </td>
              </tr>
            ) : filteredActions.length > 0 ? (
              filteredActions.map((action) => (
                <tr
                  key={action.id}
                  className={getRowClass(classes, action)}
                  style={{ cursor: "pointer" }}
                  onClick={() => setEditingAction(action)}
                >
                  <td className={classes.td} style={{ fontWeight: 500 }}>
                    {action.actionDescription}
                  </td>
                  <td className={classes.td}>
                    <Chip
                      size="small"
                      label={sourceLabels[action.source] || action.source}
                      className={getSourceBadgeClass(classes, action.source)}
                      style={{ padding: '0 3px' }}
                    />
                  </td>
                  <td className={classes.td} style={{ color: "#111827" }}>
                    {getAssignedName(action)}
                  </td>
                  <td className={classes.td} style={{
                    color: action.status === ACTION_STATUS.OVERDUE ? "#DC2626" : "#111827",
                    fontWeight: 400,
                  }}>
                    {formatDate(action.dueDate)}
                  </td>
                  <td className={classes.td}>
                    <Chip
                      size="small"
                      label={statusLabels[action.status] || action.status}
                      className={getStatusBadgeClass(classes, action.status)}
                      style={{ padding: '0 3px' }}
                    />
                  </td>
                  <td className={classes.td} onClick={(e) => e.stopPropagation()}>
                    {action.fileUrl ? (
                      <Chip
                        size="small"
                        label={action.fileName || "Attached"}
                        style={{ padding: '0 3px', cursor: "pointer" }}
                        className={classes.badgeTeal}
                        onClick={() => {
                          setPreviewFileUrl(action.fileUrl);
                          setOpenPreviewDialog(true);
                        }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="Attach"
                        style={{ padding: '0 3px' }}
                        className={classes.badgeGrey}
                      />
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className={classes.emptyCell}>
                  No actions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      <AddActionDialog
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onConfirm={handleAddAction}
        companyUserOptions={companyUserOptions}
        workerOptions={workerOptions}
      />

      <EditActionDialog
        open={!!editingAction}
        action={editingAction}
        onClose={() => setEditingAction(null)}
        onConfirm={handleUpdateAction}
      />

      <PreviewDialog
        open={openPreviewDialog}
        setOpen={setOpenPreviewDialog}
        fileUrl={previewFileUrl}
      />
    </Box>
  );
};

export default ActionRegisterTable;
